import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import type { AlgoOrder } from '@/store/orders/algoOrders/state.ts';
import type { CallOrder } from '@/store/orders/call/state.ts';
import type { FixingOrder } from '@/store/orders/fixing/state.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import type { StopLossOrder } from '@/store/orders/stopLoss/state.ts';
import type { TakeProfitOrder } from '@/store/orders/takeProfitOrder/state.ts';
import type { ClientWay } from '@/store/orders/types.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

type OrderClientWayProps = {
  clientWayData:
    | {
        clientWay: ClientWay | undefined;
        notionalCurrencyIndex: CurrencyIndex;
        currency: string | undefined;
      }
    | undefined;
  validate: (
    updatedProperties:
      | Partial<StopLossOrder['properties']>
      | Partial<TakeProfitOrder['properties']>
      | Partial<CallOrder['properties']>
      | Partial<FixingOrder['properties']>
      | Partial<AlgoOrder['properties']>,
  ) => Promise<
    | {
        data: ValidateOrderResponse;
        error: undefined;
      }
    | {
        data: undefined;
        error: FetchBaseQueryError | SerializedError;
      }
    | undefined
  >;
};

export const OrderClientWay = ({ clientWayData, validate }: OrderClientWayProps) => {
  const { clientWay, notionalCurrencyIndex, currency } = clientWayData ?? {};
  const track = useTrackEvent();

  const handleChangeClientWay = (event: React.ChangeEvent<HTMLSelectElement>) => {
    track('Order action', 'Client way changed');
    return validate({
      clientWay: event.target.value as 'sell' | 'buy',
    });
  };

  return (
    <OrderRow labelId="order.fields.clientWay.label">
      <Form.Select value={clientWay} onChange={handleChangeClientWay} size="sm" data-e2e="client-way-select">
        <option value="sell">
          {notionalCurrencyIndex === 1 ? (
            <FormattedMessage id="order.fields.clientWay.sellItem" />
          ) : (
            <FormattedMessage id="order.fields.clientWay.buyItem" />
          )}{' '}
          {currency}
        </option>
        <option value="buy">
          {notionalCurrencyIndex === 1 ? (
            <FormattedMessage id="order.fields.clientWay.buyItem" />
          ) : (
            <FormattedMessage id="order.fields.clientWay.sellItem" />
          )}{' '}
          {currency}
        </option>
      </Form.Select>
    </OrderRow>
  );
};
